/**
 * useModalAccessibility - Focus trap and keyboard handling for modals
 *
 * Provides:
 * - Escape key to close
 * - Focus trap (Tab cycles within modal)
 * - Auto-focus first focusable element on open
 * - Restore focus to trigger element on close
 */

import { useEffect, useRef, useCallback } from 'react';

const FOCUSABLE_SELECTOR =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

interface UseModalAccessibilityOptions {
  /** Whether the modal is currently open */
  isOpen: boolean;
  /** Callback to close the modal */
  onClose: () => void;
  /** Whether to auto-focus the first focusable element (default: true) */
  autoFocus?: boolean;
  /**
   * Suppress the Escape-to-close behaviour while a CHILD modal is stacked on
   * top. Document-level keydown listeners can't stop each other with
   * stopPropagation (only stopImmediatePropagation would, and listener order
   * is mount order) — so a parent modal must opt out of Escape while a child
   * that registers its own handler is open, or one Esc press closes both.
   */
  escapeDisabled?: boolean;
}

/**
 * Hook to add accessibility behavior to modals.
 *
 * @returns A ref to attach to the modal container element
 *
 * @example
 * ```tsx
 * function MyModal({ isOpen, onClose }) {
 *   const modalRef = useModalAccessibility({ isOpen, onClose });
 *   if (!isOpen) return null;
 *   return (
 *     <div className="fixed inset-0 z-50" role="dialog" aria-modal="true">
 *       <div ref={modalRef}>
 *         ...content...
 *       </div>
 *     </div>
 *   );
 * }
 * ```
 */
export function useModalAccessibility({
  isOpen,
  onClose,
  autoFocus = true,
  escapeDisabled = false,
}: UseModalAccessibilityOptions) {
  const modalRef = useRef<HTMLDivElement>(null);
  const previousFocusRef = useRef<HTMLElement | null>(null);

  // Store the element that had focus before the modal opened
  useEffect(() => {
    if (isOpen) {
      previousFocusRef.current = document.activeElement as HTMLElement;
    }
  }, [isOpen]);

  // Auto-focus the modal CONTAINER when it opens (dialog pattern). Focusing
  // the first focusable element painted a visible focus ring on whatever
  // happened to be first — e.g. the customer-name link in the fault modal
  // header lit up with a gold ring on every open. Container focus keeps
  // keyboard flow intact (Tab reaches the first control, Escape closes)
  // without the stray ring.
  useEffect(() => {
    if (!isOpen || !autoFocus || !modalRef.current) return;

    // Small delay to let the modal render
    const timer = requestAnimationFrame(() => {
      const el = modalRef.current;
      if (!el) return;
      if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '-1');
      el.style.outline = 'none';
      el.focus({ preventScroll: true });
    });

    return () => cancelAnimationFrame(timer);
  }, [isOpen, autoFocus]);

  // Restore focus when modal closes
  useEffect(() => {
    if (!isOpen && previousFocusRef.current) {
      previousFocusRef.current.focus();
      previousFocusRef.current = null;
    }
  }, [isOpen]);

  // Escape key handler
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!isOpen) return;

      if (e.key === 'Escape') {
        if (escapeDisabled) return;
        e.preventDefault();
        e.stopPropagation();
        onClose();
        return;
      }

      // Focus trap: Tab / Shift+Tab cycles within modal
      if (e.key === 'Tab' && modalRef.current) {
        const focusableElements = modalRef.current.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR);
        if (focusableElements.length === 0) return;

        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey && document.activeElement === firstElement) {
          e.preventDefault();
          lastElement.focus();
        } else if (!e.shiftKey && document.activeElement === lastElement) {
          e.preventDefault();
          firstElement.focus();
        }
      }
    },
    [isOpen, onClose, escapeDisabled]
  );

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  return modalRef;
}
